import { Constants, CodeAndName } from '@/utils/constants';
import Router, { NextRouter } from 'next/router';
import { getCredentials } from './cognito';

/**
 * アプリ全体で利用する表示変換・画面遷移補助の共通ユーティリティ群。
 * APIレスポンスのコード値を画面表示用文字列へ変換したり、
 * ログイン後のリダイレクト先を安全に扱うための処理をここに集約している。
 */

/**
 * 支払い方法コードを画面表示用の文言へ変換する。
 * APIレスポンスではコード値で管理されているため、画面側ではこの関数を通して
 * 利用者向けの文言へ正規化する。
 *
 * @param type 支払い方法コード
 * @returns 表示用の支払い方法名
 */
export const getPaymentTypeText = (type: string): string => {
  const result: CodeAndName | undefined = Object.values(Constants.PaymentMethod).find(
    (item: CodeAndName): boolean => {
      return item.code === type;
    }
  );
  if (result === undefined) {
    return '読み込めません';
  }

  return 'nameShort' in result ? <string>result.nameShort : result.name;
};

/**
 * 「請求情報」の「支払状況」の表示用文字列を返却する。
 * @param status 請求状態コード
 * @returns 表示用文字列
 */
export const getPaymentStatusText = (status: string) => {
  switch (status) {
    case '0':
      return 'ご請求前';
    case '1':
      return 'ご請求中';
    case '2':
      return '一部お支払い待ち';
    case '3':
      return 'お支払い済み';
    case '4':
      return '弁護士法人ご請求中';
    case '5':
      return 'お支払い待ち';
    case '6':
      return 'ご請求停止';
    default:
      return '読み込めません';
  }
};

/**
 * オプションの利用状態コードを表示用文字列へ変換する。
 * 申込中・利用中・解約中などの状態をUI上で統一して扱うために使用する。
 *
 * @param status オプション状態コード
 * @returns 表示用文字列
 */
export const getOptionStatusText = (status: string) => {
  switch (status) {
    case Constants.OptionStatus.NONE:
      return '未申込';
    case Constants.OptionStatus.ENTRY:
      return '申込中';
    case Constants.OptionStatus.SERVICE:
      return '利用中';
    case Constants.OptionStatus.CANCEL:
      return 'キャンセル';
    case Constants.OptionStatus.QUIT:
      return '解約済';
    case Constants.OptionStatus.QUIT_WITHOUT:
      return '解約中';
    default:
      return '読み込めません';
  }
};

/**
 * VoIPサービスパックコードを表示用の名称へ変換する。
 * 申込内容確認画面や契約情報画面で、コード値ではなく利用者が理解できる名称を表示するために使う。
 *
 * @param pack サービスパックコード
 * @returns 表示用文字列
 */
export const getVoipOptionServicePackText = (pack: string) => {
  switch (pack) {
    case Constants.VoipOptionServicePack.SERVICE_PACK_NONE:
      return 'サービスパック無し';
    case Constants.VoipOptionServicePack.SERVICE_PACK_1:
      return 'サービスパック1';
    case Constants.VoipOptionServicePack.SERVICE_PACK_2:
      return 'サービスパック2';
    case Constants.VoipOptionServicePack.SERVICE_PACK_3:
      return 'サービスパック3';
    case Constants.VoipOptionServicePack.SERVICE_PACK_4:
      return 'サービスパック4';
    case Constants.VoipOptionServicePack.SERVICE_PACK_5:
      return 'サービスパック5';
    default:
      return '読み込めません';
  }
};

/**
 * 0ABJ番号の取得方式を表示用文字列へ変換する。
 * NURO 光 でんわの申込内容確認で、新規発番か番号ポータビリティかを明示するために使用する。
 *
 * @param type 発番方式コード
 * @returns 表示用文字列
 */
export const getVoipSource0abjTypeText = (type: number) => {
  switch (type) {
    case Constants.VoipSource0abjType.NEW_ISSUE:
      return '新規発番';
    case Constants.VoipSource0abjType.NUMBER_PORTABILITY:
      return '番号ポータビリティ';
    default:
      return '-';
  }
};

/**
 * オプション手続き区分を表示用文字列へ変換する。
 * 申込・解約・変更などの種別を履歴や確認画面で読みやすくするために使用する。
 *
 * @param process オプション手続き種別コード
 * @returns 表示用文字列
 */
export const getOptionProcessText = (process: number) => {
  switch (process) {
    case Constants.OptionOrderType.SIMPLE_ENTRY:
      return '申込';
    case Constants.OptionOrderType.SIMPLE_ENTRY_CANCEL:
      return '申込キャンセル';
    case Constants.OptionOrderType.SIMPLE_QUIT:
      return '解約';
    case Constants.OptionOrderType.SIMPLE_QUIT_CANCEL:
      return '解約キャンセル';
    case Constants.OptionOrderType.CONTENT_CHANGE:
      return '変更';
    case Constants.OptionOrderType.SAME_TIME_ENTRY:
      return '回線同時申込';
    default:
      return '読み込めません';
  }
};

/**
 * amuelinkの利用状態コードを表示用文字列へ変換する。
 * amuelink専用の状態コード体系を、他のオプション表示と同じ見せ方にそろえるために使用する。
 *
 * @param status amuelink状態コード
 * @returns 表示用文字列
 */
export const getAmuelinkStatusText = (status: string) => {
  switch (status) {
    case Constants.AmuelinkStatus.ENTRY:
      return '申込中';
    case Constants.AmuelinkStatus.SERVICE:
      return '利用中';
    case Constants.AmuelinkStatus.CANCEL:
      return 'キャンセル';
    case Constants.AmuelinkStatus.QUIT:
      return '解約済';
    case Constants.AmuelinkStatus.QUIT_WITHOUT:
      return '解約中';
    default:
      return '読み込めません';
  }
};

/**
 * 単品付加サービス(番号表示)を含むサービスパックの判定
 * VoIP 変更画面で、単品チェックボックスを操作可能にしてよいか判断するために使う。
 * @param pack '01', '02', '03', '04', '05'
 * @return 含まれる場合：true、含まれない場合：false
 */
export const containNumberDisplay = (pack: string): boolean => {
  return !!(pack === '01' || pack === '03' || pack === '05');
};

/**
 * 単品付加サービス(番号リクエスト)を含むサービスパックの判定
 * サービスパック契約中は単品申込と二重管理しないため、内包判定を共通化する。
 * @param pack '01', '02', '03', '04', '05'
 * @return 含まれる場合：true、含まれない場合：false
 */
export const containNumberRequest = (pack: string): boolean => {
  return !!(pack === '01' || pack === '03' || pack === '05');
};

/**
 * 単品付加サービス(キャッチ電話)を含むサービスパックの判定
 * パック契約時の表示・送信分岐を一箇所で判定できるようにする。
 * @param pack '01', '02', '03', '04', '05'
 * @return 含まれる場合：true、含まれない場合：false
 */
export const containCatchPhone = (pack: string): boolean => {
  return !!(pack === '01' || pack === '02' || pack === '03' || pack === '04');
};

/**
 * 単品付加サービス(着信お断り)を含むサービスパックの判定
 * 画面側で pack コード一覧を直接持たずに済むよう、意味名で判定できるようにする。
 * @param pack '01', '02', '03', '04', '05'
 * @return 含まれる場合：true、含まれない場合：false
 */
export const containStopNuisanceCall = (pack: string): boolean => {
  return !!(pack === '01' || pack === '02' || pack === '03' || pack === '04');
};

/**
 * 単品付加サービス(着信転送)を含むサービスパックの判定
 * パック契約有無で単品の見せ方と送信値が変わるため、専用判定を切り出している。
 * @param pack '01', '02', '03', '04', '05'
 * @return 含まれる場合：true、含まれない場合：false
 */
export const containVoiceWarp = (pack: string): boolean => {
  return !!(pack === '01' || pack === '02');
};

/**
 * ログイン判定
 * 未ログイン画面一覧と認証情報有無をまとめて判定し、ヘッダー/フッター表示制御に使う。
 * @param pathName next/routerのpathname
 * @return ログインしている場合：true、ログインしていない場合：false
 */
export const checkIsLogin = (pathName: string): boolean => {
  return (
    !Constants.PathList.NOT_LOGIN.some((path) => path.startsWith(pathName)) &&
    getCredentials() !== null
  );
};

/**
 * クレジットカード有効期限（年）リスト作成
 * 現在年から将来20年分を生成し、カード期限プルダウンの候補に使う。
 * @return 20年後までの有効期限（年）リスト
 */
export const getCreditCardExpireYearList = () => {
  const expireYear: string[] = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  for (let i = 0; i < 20; i += 1) {
    expireYear.push(String(currentYear + i));
  }

  return expireYear;
};

/**
 * パスパラメータにserviceIdを埋め込む
 * ログイン時など、非同期のためにuseInsertServiceIdInApiUrlでの変換時にserviceIdの取得が間に合わない場合に利用
 * @param apiUrl API URL
 * @param serviceId サービスID
 * @returns サービスIDを埋め込んだURL
 */
export const insertServiceIdInApiUrlSync = (apiUrl: string, serviceId: string) => {
  const apiUrlWithServiceId = apiUrl.replace(':service_id', serviceId);

  return apiUrlWithServiceId;
};

/**
 * URIからクエリパラメータを抽出する
 * @param uri URI
 * @returns クエリパラメータ
 */
export const extractSearchParams = (uri: string): URLSearchParams | null => {
  if (!uri) return null;

  const paramStart = uri.indexOf('?');
  if (paramStart === -1) return null;
  return new URLSearchParams(uri.substring(paramStart + 1));
};

/**
 * リダイレクト先URIバリデーションチェック
 * @param uri URI (クエリパラメータはURLエンコードされていることを想定)
 * @returns リダイレクト先のURIのフォーマットが正しければそのURIを返す (デコード済み)
 */
export const validateRedirectUri = (uri: string) => {
  // URIが '' | null | undefined であれば無効
  if (!uri) return '';

  // クエリパラメータをパース
  const params = extractSearchParams(uri);
  if (!params) return '';
  const redirectUri = params.get('redirect_uri') ?? '';

  // フォーマット確認 (内部パス or 外部URL)
  if (!(redirectUri.startsWith('/') || redirectUri.startsWith('http'))) return '';

  // 外部URLの場合、リダイレクト可能なホスト名を限定
  if (redirectUri.startsWith('http')) {
    const url = new URL(redirectUri);
    const allowedHostPattern = /.*nuro\.jp$/; // nuro.jpドメインのみ許可
    if (!allowedHostPattern.test(url.hostname)) return '';
  }

  return redirectUri;
};

/**
 * 内部パスであればrouter.pushを用いてページ遷移を行う
 * 外部URLであればwindow.location書き換えによりリダイレクトを行う
 * @param router Nextルータ
 * @param uri リダイレクト先URI
 */
export const dispatchRedirect = (router: NextRouter, uri: string) => {
  if (uri.startsWith('/')) {
    // 内部パスへのページ遷移
    router.push(uri);
  } else if (uri.startsWith('http')) {
    // 外部URLへのリダイレクト
    window.location.href = uri;
  }
};

/**
 * リダイレクト先URIをURLに付加する
 * @param url 元のURL
 * @param redirectUri リダイレクト先URI
 * @returns リダイレクト先URIを付加したURL
 */
export const attachRedirectUri = (
  url: string,
  redirectUri: string | { [key: string]: string | null | undefined }
): string => {
  // URLが '' | null | undefined であれば無効
  if (!url) return '';
  // redirectUriが '' | null | undefined の場合は元のURLを返す
  if (!redirectUri) return url;
  // redirectUriが空オブジェクトの場合は元のURLを返す
  if (Object.values(redirectUri).filter((v) => !!v).length === 0) return url;

  // ベースURL
  const urlBase = url.split('?')[0];

  // クエリパラメータをパース
  // 既存パラメータ無しの場合は空のURLSearchParamsを生成
  const params = extractSearchParams(url) ?? new URLSearchParams();
  // redirect_uriを設定
  if (typeof redirectUri === 'string') {
    params.set('redirect_uri', redirectUri);
  } else {
    // 複数の戻り先関連パラメータを扱えるよう、key-value 形式も受け付ける。
    Object.entries(redirectUri).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
  }

  // toStringによりクエリパラメータを文字列化 & エンコード
  const paramsString = params.toString();
  if (!paramsString) return urlBase;
  return `${urlBase}?${paramsString}`;
};

/**
 * router.pushによるページ遷移の際にリダイレクト先URIの情報を引き渡す
 * @param router Nextルータ
 * @param url 遷移先パス
 * @param redirectUri リダイレクト先URI
 */
export const passRedirectUri = (
  router: NextRouter,
  url: string,
  redirectUri: string | { [key: string]: string | null | undefined }
) => {
  router.push(attachRedirectUri(url, redirectUri));
};

/**
 * next/routerのqueryがstringの場合、その値を返す
 * @param query next/routerのquery
 * @returns stringの場合のquery
 */
export const getParamsIfQueryIsString = (query: string | string[] | undefined) => {
  if (!query) return '';

  // 配列 query は呼び出し元の想定外なので、空文字に寄せて安全に無視する。
  if (typeof query !== 'string') return '';

  return query;
};

/**
 * 引数のパスが現在のページと一致するかチェックする
 * @param path チェック対象パス
 * @returns 現在のページと引数のパスが一致する場合、trueを返す
 */
export const isMatchCurrentPage = (path: string) => {
  const currentPage = Router.pathname;

  const removedTrailingSlashCurrentPage = currentPage.replace(/\/$/, '');
  const removedTrailingSlashTargetPath = path.replace(/\/$/, '');

  if (removedTrailingSlashCurrentPage === removedTrailingSlashTargetPath) return true;

  return false;
};

/**
 * 秒数を〇〇時間・〇〇分の形式に変換する
 * @param second 秒
 * @returns 〇〇時間・〇〇分
 */
export const convertSecondToTime = (second: number): string => {
  const hours: number = Math.floor(second / 3600);
  const minutes: number = Math.floor((second % 3600) / 60);

  return `${hours}時間 ${minutes}分`;
};

/**
 * アクセス先画面のパスを取得する
 * <br>
 * URLに指定された(to=)の値を返す。
 * @param uri URI(router.asPathを想定)
 * @returns アクセス画面へのパス
 */
export const getAccessUri = (uri: string) => {
  if (!uri.includes('to=')) {
    return '';
  }

  // `to=` 以降の最初の値だけを抜き出し、余分なクエリを含めずに画面判定へ渡す。
  // eslint-disable-next-line prefer-destructuring, @typescript-eslint/no-unused-vars
  const accessUrl = uri.split('to=')[1].split('&')[0];

  return accessUrl;
};
